import React, { useState, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getUserId } from '../../userLocalDetails';
import PlusBlue from '../../static/img/Plus-blue.svg';

import {
  LeftConatiner,
  LeftConatinerHeading,
  LeftConatinerHeadingText,
  MainConatiner,
  PlusImage,
  RightConatiner,
  RightConatinerHeading,
  RightConatinerHeadingText,
  SpecificComment,
  SpecificCommentText,
  SpecificCommentsCont,
} from './feedbackAreastyle';
import CommentSuggestion from './CommentSuggestion';
import FeedbackAreasDragDrop from './FeedbackAreasDragDrop';
import Toast from '../Toast';
import { toast } from 'react-toastify';

function FeedbackArea(props) {
  const {
    smartAnnotation,
    UpdateSmartAnotationHandler,
    deleteAnnotationHandler,
    createSmartAnnotation,
    smartAnnotationeditIndex,
    createSmartAnnotationHandler,
    reorderSmartCommentsHandler,
  } = props;

  const [currentSmartCommentId, setCurrnetSmartCommentId] = useState(
    smartAnnotationeditIndex,
  );
  const [pendingSuggestions, setPendingSuggestions] = useState([]);

  const baseSmartComment = smartAnnotation?.smartComments?.[currentSmartCommentId];
  const currentSmartComment = baseSmartComment ? {
    ...baseSmartComment,
    suggestions: [...(baseSmartComment.suggestions || []), ...pendingSuggestions]
  } : null;

  const saveEditedSuggestion = (updatedText, index) => {
    if (updatedText.length === 0) {
      toast(<Toast message={'Please add some text to the new comment'} />);
      return;
    }
    const freshSmartComment = smartAnnotation?.smartComments?.[currentSmartCommentId];
    const baseSuggestions = freshSmartComment?.suggestions || [];
    const allSuggestions = [...baseSuggestions, ...pendingSuggestions];
    allSuggestions[index] = updatedText;

    const newSmartAnnotation = {
      ...freshSmartComment,
      suggestions: allSuggestions
    };
    UpdateSmartAnotationHandler(newSmartAnnotation, currentSmartCommentId);
    setPendingSuggestions([]);
  };

  const handleDeleteSuggestion = (index) => {
    const freshSmartComment = smartAnnotation?.smartComments?.[currentSmartCommentId];
    const baseSuggestions = freshSmartComment?.suggestions || [];
    const allSuggestions = [...baseSuggestions, ...pendingSuggestions];
    allSuggestions.splice(index, 1);

    const newSmartAnnotation = {
      ...freshSmartComment,
      suggestions: allSuggestions
    };
    UpdateSmartAnotationHandler(newSmartAnnotation, currentSmartCommentId);
    setPendingSuggestions([]);
  };

  const addNewSuggestions = () => {
    setPendingSuggestions([...pendingSuggestions, '']);
  };

  const handleSmartCommentSelection = (_smartComment, index) => {
    setCurrnetSmartCommentId(index);
    setPendingSuggestions([]);
  };

  const cleanSmartComments = smartAnnotation?.smartComments?.filter(comment => comment != null) || [];
  const canEdit = smartAnnotation?.ownerId === getUserId();

  const suggestions = currentSmartComment?.suggestions || [];
  const VIRTUALIZE_THRESHOLD = 50;
  const shouldVirtualize = suggestions.length > VIRTUALIZE_THRESHOLD;

  const scrollContainerRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: suggestions.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 57,
    enabled: shouldVirtualize,
  });

  const renderSuggestionsList = () => {
    if (shouldVirtualize) {
      return (
        <SpecificCommentsCont ref={scrollContainerRef}>
          <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const comment = suggestions[virtualRow.index];
              return (
                <div
                  key={virtualRow.index}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <CommentSuggestion
                    comment={comment}
                    index={virtualRow.index}
                    saveEditedSuggestion={saveEditedSuggestion}
                    handleDeleteSuggestion={handleDeleteSuggestion}
                    defaultEditing={
                      comment.length === 0 &&
                      suggestions.length === virtualRow.index + 1
                    }
                  />
                </div>
              );
            })}
          </div>
          {canEdit && (
            <SpecificComment
              style={{ justifyContent: 'flex-start', gap: '8px' }}
              onClick={addNewSuggestions}
            >
              <PlusImage src={PlusBlue} />
              <SpecificCommentText
                style={{ color: '#7200E0', fontWeight: '500' }}
              >
                Add New Comment
              </SpecificCommentText>
            </SpecificComment>
          )}
        </SpecificCommentsCont>
      );
    }

    return (
      <SpecificCommentsCont>
        {suggestions.map((comment, index) => (
          <CommentSuggestion
            key={index}
            comment={comment}
            index={index}
            saveEditedSuggestion={saveEditedSuggestion}
            handleDeleteSuggestion={handleDeleteSuggestion}
            defaultEditing={
              comment.length === 0 &&
              suggestions.length === index + 1
            }
          />
        ))}
        {canEdit && (
          <SpecificComment
            style={{ justifyContent: 'flex-start', gap: '8px' }}
            onClick={addNewSuggestions}
          >
            <PlusImage src={PlusBlue} />
            <SpecificCommentText
              style={{ color: '#7200E0', fontWeight: '500' }}
            >
              Add New Comment
            </SpecificCommentText>
          </SpecificComment>
        )}
      </SpecificCommentsCont>
    );
  };

  return (
    <>
      <MainConatiner>
        <LeftConatiner>
          <LeftConatinerHeading>
            <LeftConatinerHeadingText>Feedback Areas</LeftConatinerHeadingText>
          </LeftConatinerHeading>
          <FeedbackAreasDragDrop
            smartComments={cleanSmartComments}
            currentSmartCommentId={currentSmartCommentId}
            handleSmartCommentSelection={handleSmartCommentSelection}
            UpdateSmartAnotationHandler={UpdateSmartAnotationHandler}
            createSmartAnnotation={createSmartAnnotation}
            deleteAnnotationHandler={deleteAnnotationHandler}
            reorderSmartCommentsHandler={reorderSmartCommentsHandler}
            canEdit={canEdit}
            createSmartAnnotationHandler={createSmartAnnotationHandler}
            hasEmptyPendingComment={pendingSuggestions.some(s => !s?.trim())}
          />
        </LeftConatiner>
        <RightConatiner>
          <RightConatinerHeading>
            <RightConatinerHeadingText>
              Specific Comments:
              <span style={{ opacity: '0.7' }}>
                {' '}
                {currentSmartComment?.title}
              </span>
            </RightConatinerHeadingText>
          </RightConatinerHeading>
          {renderSuggestionsList()}
        </RightConatiner>
      </MainConatiner>
    </>
  );
}

export default React.memo(FeedbackArea);
